// src/lib/memberTime.js
//
// Time zones for the time tracker. Each team member has their own zone
// (businesses/{uid}.timeZone, set by them or a super admin), and every
// session stores the zone it was worked in. So "which day were these hours"
// and "what time did they start" are always answered in the member's local
// time — not UTC, and not whoever happens to be looking.
import { browserTimeZone, dateKey, fmtTime, tzAbbrev, timezoneOptions } from "./bookingTime";

export { browserTimeZone, timezoneOptions };

export function validTz(tz) {
  if (!tz || typeof tz !== "string") return false;
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return true; } catch { return false; }
}

const ms = v => (v?.toMillis ? v.toMillis() : typeof v === "number" ? v : new Date(v).getTime());

// A member's zone: what's saved on their profile, else this browser's.
export const tzOf = profile => (validTz(profile?.timeZone) ? profile.timeZone : browserTimeZone());

// The zone a session belongs to: stored on it at clock-in; older sessions
// fall back to the member's current zone, then the viewer's.
export const entryTz = (entry, fallback) =>
  validTz(entry?.timeZone) ? entry.timeZone : validTz(fallback) ? fallback : browserTimeZone();

// "2026-10-13" — the member's calendar day for an instant.
export function dayIn(value, tz) {
  const t = ms(value);
  return Number.isFinite(t) ? dateKey(t, tz) : "";
}

// "9:05 AM"
export const timeIn = (value, tz) => fmtTime(ms(value), tz);

// "9:05 AM PKT"
export const timeWithZone = (value, tz) => `${fmtTime(ms(value), tz)} ${tzAbbrev(ms(value), tz)}`;

// "Oct 13, 9:05 AM PKT"
export const dateTimeWithZone = (value, tz) =>
  `${new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(ms(value)))} ${tzAbbrev(ms(value), tz)}`;

export const zoneLabel = (tz, at = Date.now()) => `${tz.replace(/_/g, " ")} (${tzAbbrev(at, tz)})`;

// Day keys are plain calendar labels, so UTC arithmetic is exact for them.
export function niceDayKey(key, opts = { weekday: "long", month: "short", day: "numeric" }) {
  const d = new Date(`${key}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString("en-US", { ...opts, timeZone: "UTC" });
}
export function shiftDayKey(key, n) {
  const d = new Date(`${key}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// How far `tz` is ahead of UTC at a given instant, in ms.
function offsetAt(t, tz) {
  const p = {};
  for (const { type, value } of new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(t))) p[type] = value;
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return asUtc - Math.floor(t / 1000) * 1000;
}

// Wall-clock "2026-10-13" + "09:00" in `tz` → the real instant (ms).
export function zonedToUtc(day, hhmm, tz) {
  const [y, m, d] = String(day).split("-").map(Number);
  const [h, mi] = String(hhmm || "00:00").split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, h || 0, mi || 0);
  if (!Number.isFinite(guess)) return NaN;
  let t = guess - offsetAt(guess, tz);
  t = guess - offsetAt(t, tz); // second pass settles DST edges
  return t;
}

// The instant range [start, end) covering a member's calendar day.
export const dayRangeUtc = (day, tz) => [zonedToUtc(day, "00:00", tz), zonedToUtc(shiftDayKey(day, 1), "00:00", tz)];
